import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Grid, makeStyles } from "@material-ui/core";
import { useLoanStore } from '@roc/feature-loans';
import { ConfirmDialog, Paper, PaperBorderType, SelectField } from '@roc/ui';

const useStyles = makeStyles(() => ({
  gridContainer: {
    padding: '0 16px 16px 16px',
  },
  selectFieldContainer: {
    '& .MuiFormControl-root.MuiFormControl-fullWidth': {
      padding: '0 5px 0 10px',
      borderRadius: '5px',
      border: '1px solid #E4E4E7 !important',
      '& .MuiInput-underline': {
        '&:before': {
          transition: 'none !important',
          borderBottom: 'none !important',
        },
        '&:after': {
          transform: 'none !important',
          transition: 'none !important',
          borderBottom: 'none !important',
        },
      },
    },
  },
}));

interface SpecialistOption {
  label: string;
  value: string;
}

interface Loan {
  loanId: number;
  extensionSpecialistFullName?: string;
}

interface SpecialistsDropdown {
  extensionSpecialists?: SpecialistOption[];
}

interface LoanActorsProps {
  loan?: Loan;
  specialistsDropdown?: SpecialistsDropdown;
}

export default function ExtensionsLoanActors({ loan, specialistsDropdown }: LoanActorsProps) {
  const classes = useStyles();
  const { loanStore } = useLoanStore();

  const [selectedExtensionSpecialist, setSelectedExtensionSpecialist] = useState<SpecialistOption | undefined | null>();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  const extensionSpecialistOptions = useMemo(() => specialistsDropdown?.extensionSpecialists, [specialistsDropdown]);

  const handleConfirmRef = useRef<() => Promise<void>>();

  const updateSpecialists = useCallback((extensionSpecialistOption?: SpecialistOption) => {
    return loanStore.updateSpecialists(loan?.loanId,
      {
        extensionSpecialistId: extensionSpecialistOption?.value ? +extensionSpecialistOption?.value : undefined,
      });
  }, [loanStore, loan]);

  const confirmUpdateSpecialists = useCallback(async (extensionSpecialist?: SpecialistOption) => {
    updateSpecialists(extensionSpecialist);

    setSelectedExtensionSpecialist((prevExtensionSpecialist) => extensionSpecialist || prevExtensionSpecialist);

    setIsConfirmOpen(false);
    setDialogMessage('');
  }, [updateSpecialists]);

  const openDialog = useCallback((specialistType: string, currentSpecialistName: string, newSpecialistName: string, extensionSpecialist?: SpecialistOption) => {
    setDialogMessage(`Are you sure you want to change the ${specialistType} Specialist from ${currentSpecialistName} to ${newSpecialistName}?`);
    setIsConfirmOpen(true);

    return () => {
      return confirmUpdateSpecialists(extensionSpecialist);
    };
  }, [confirmUpdateSpecialists]);

  const findSpecialistOption = (specialistValue: string, prop: keyof SpecialistOption, specialistOptions: SpecialistOption[]) => {
    return specialistOptions.find(specialist => specialist[prop] === specialistValue);
  };

  const handleExtensionSpecialistChange = useCallback((optionValue?: string) => {
    const specialist = findSpecialistOption(optionValue, 'value', extensionSpecialistOptions);

    if (specialist && specialist !== selectedExtensionSpecialist) {
      handleConfirmRef.current = openDialog('Extension', selectedExtensionSpecialist?.label ?? 'none', specialist.label, specialist);
    }
  }, [selectedExtensionSpecialist, extensionSpecialistOptions, openDialog]);

  useEffect(() => {
    if (loan && extensionSpecialistOptions && !selectedExtensionSpecialist) {
      const extensionSpecialist = findSpecialistOption(loan.extensionSpecialistFullName, 'label', extensionSpecialistOptions);

      if (extensionSpecialist) {
        setSelectedExtensionSpecialist(extensionSpecialist);
      }
    }
  }, [loan, selectedExtensionSpecialist, extensionSpecialistOptions]);

  return (
    <>
      <Paper title="Loan Actors" borderType={PaperBorderType.SOLID_BORDER}>
        <Grid
          container
          justifyContent='space-between'
          spacing={2}
          className={classes.gridContainer}
        >
          <Grid item xs={12} md={12} className={classes.selectFieldContainer}>
            {extensionSpecialistOptions &&
              <SelectField
                standaloneLabel
                label='Extension Specialist'
                value={selectedExtensionSpecialist?.value ?? ''}
                options={extensionSpecialistOptions}
                onChange={handleExtensionSpecialistChange}
                fullWidth
                testId='extension-specialist-select'
              />
            }
          </Grid>
        </Grid>
      </Paper>
      <ConfirmDialog
        open={isConfirmOpen}
        title={'Update Specialist'}
        body={dialogMessage}
        handleCancel={() => setIsConfirmOpen(false)}
        handleClose={() => setIsConfirmOpen(false)}
        handleConfirm={handleConfirmRef.current}
        confirmButtonText={'Yes'}
        cancelButtonText={'No'}
      />
    </>
  );
}
